import config from "@Config/index";

export const EXPANDED_RADIUS_METERS = 20000;

/**
 * GrowthBook RADIUS_EXPAND is client-only and defaults to false during SSR. Direct landings on
 * uni/locality search need the geo-radius flow on the first fetch, so treat the flag as enabled
 * on the server (still off in China). On the client, use the Redux value set from GrowthBook.
 */
export const resolveRadiusExpandEnabled = (enabled = false): boolean => {
  if (config.IS_CHINA_APP) return false;
  if (!__CLIENT__) return true;
  return enabled;
};

type GeoFilters = {
  university?: string | string[];
  locality?: string | string[];
  region_radius?: string | number | string[];
  [key: string]: any;
};

type GeoRegion = {
  region_type?: string;
};

type InventoryWithId = {
  id?: string | number;
};

export const isNearbyLocality = (locality?: string | string[]) =>
  Boolean(locality && String(locality).includes("near-by"));

export const hasExplicitDistanceFilter = (filters: GeoFilters = {}) =>
  Boolean(filters.region_radius);

export const isUniversityGeo = (filters: GeoFilters = {}, region: GeoRegion = {}) =>
  Boolean(filters.university) || region.region_type === "establishment";

export const isLocalityGeo = (filters: GeoFilters = {}, region: GeoRegion = {}) => {
  if (isNearbyLocality(filters.locality)) return false;
  return Boolean(filters.locality) || region.region_type === "sublocality";
};

/** University filter, locality filter (not near-by), or both — including uni/locality landings. */
export const hasUniversityOrLocalitySelected = (filters: GeoFilters = {}, region: GeoRegion = {}) =>
  isUniversityGeo(filters, region) || isLocalityGeo(filters, region);

/**
 * Gatekeeper for the whole radius-expand feature. Pass `resolveRadiusExpandEnabled(reduxFlag)`
 * so SSR direct landings work; on the client the GrowthBook mirror in Redux applies.
 */
export const isGeoRadiusExpandFlow = (
  filters: GeoFilters = {},
  region: GeoRegion = {},
  enabled = false,
) => {
  if (!enabled) return false;
  if (isNearbyLocality(filters.locality)) return false;
  if (hasExplicitDistanceFilter(filters)) return false;
  return hasUniversityOrLocalitySelected(filters, region);
};

export const GLOBAL_REQUEST_SOURCE_HEADER = {
  "X-Request-Source": "global",
} as const;

export const shouldSendGlobalRequestSourceHeader = ({
  filters,
  region,
  enabled = false,
  // isExpanded = false,
  isChinaApp = false,
  isWhitelabel = false,
  isApp = false,
  isMiniProgram = false,
}: {
  filters?: GeoFilters;
  region?: GeoRegion;
  enabled?: boolean;
  isExpanded?: boolean;
  isChinaApp?: boolean;
  isWhitelabel?: boolean;
  isApp?: boolean;
  isMiniProgram?: boolean;
}) => {
  if (isChinaApp || isWhitelabel || isApp || isMiniProgram) return false;
  if (!enabled) return false;
  // Uni, locality, or both — including the expand CTA (isExpanded). Java uses
  // region_radius=20000 as a cap under this header. Do not use isGeoRadiusExpandFlow:
  // an explicit region_radius filter must not drop the header while uni/locality is selected.
  return hasUniversityOrLocalitySelected(filters, region);
};

export const getGlobalRequestSourceHeader = (
  options: Parameters<typeof shouldSendGlobalRequestSourceHeader>[0],
): Record<string, string> =>
  shouldSendGlobalRequestSourceHeader(options) ? { ...GLOBAL_REQUEST_SOURCE_HEADER } : {};

/** Headers for inventory fetches that must match the list’s geo-radius behaviour (map markers). */
export const getSearchInventoriesRequestHeaders = ({
  filters,
  region,
  radiusExpandEnabled = false,
  isRadiusExpanded = false,
  isWhitelabel = false,
  isApp = false,
  isMiniProgram = false,
}: {
  filters?: GeoFilters;
  region?: GeoRegion;
  radiusExpandEnabled?: boolean;
  isRadiusExpanded?: boolean;
  isWhitelabel?: boolean;
  isApp?: boolean;
  isMiniProgram?: boolean;
}): Record<string, string> => ({
  ...(config.IS_CHINA_APP ? {} : { "X-Use-Java-Api": "true" }),
  ...getGlobalRequestSourceHeader({
    filters,
    region,
    enabled: resolveRadiusExpandEnabled(radiusExpandEnabled),
    isExpanded: isRadiusExpanded,
    isChinaApp: Boolean(config.IS_CHINA_APP),
    isWhitelabel: Boolean(config.IS_WHITELABEL_APP || isWhitelabel),
    isApp,
    isMiniProgram,
  }),
});

export const applySearchGeoRadius = (
  params: Record<string, any> = {},
  filters: GeoFilters = {},
  region: GeoRegion = {},
  isExpanded = false,
  enabled = false,
) => {
  if (!isGeoRadiusExpandFlow(filters, region, enabled)) {
    return params;
  }

  delete params.region_radius;
  delete params.locality_radius;

  // Locality searches must constrain via locality_type/circle, not region_type —
  // otherwise the API applies the circle to the parent region (e.g. university).
  if (isLocalityGeo(filters, region)) {
    delete params.region_type;
    params.locality_type = "circle";
  }

  if (!isExpanded) {
    return params;
  }

  if (isLocalityGeo(filters, region)) {
    params.locality_radius = EXPANDED_RADIUS_METERS;
  } else {
    params.region_radius = EXPANDED_RADIUS_METERS;
  }

  return params;
};

export const appendUniqueInventories = <T extends InventoryWithId>(
  existing: T[] = [],
  incoming: T[] = [],
) => {
  const seen = new Set(existing.map((item) => item?.id));
  return [...existing, ...incoming.filter((item) => item?.id != null && !seen.has(item.id))];
};

/**
 * After expand, university + X-Request-Source: global sends region_radius=20000.
 * Java treats that as a [min, 20000] band, so meta.count is nearby-only.
 * Locality expand sends locality_radius=20000 as a full circle, so meta still
 * includes the original 0–3 km results.
 */
export const isExpandMetaNearbyBandOnly = ({
  filters,
  region,
  enabled = false,
  isChinaApp = false,
  isWhitelabel = false,
  isApp = false,
  isMiniProgram = false,
}: {
  filters?: GeoFilters;
  region?: GeoRegion;
  enabled?: boolean;
  isChinaApp?: boolean;
  isWhitelabel?: boolean;
  isApp?: boolean;
  isMiniProgram?: boolean;
}) =>
  shouldSendGlobalRequestSourceHeader({
    filters,
    region,
    enabled,
    isChinaApp,
    isWhitelabel,
    isApp,
    isMiniProgram,
  }) &&
  isUniversityGeo(filters, region) &&
  !isLocalityGeo(filters, region);

/**
 * Nearby heading count. University expand meta is the 3–20 km band — do not
 * subtract primaryCount. Locality expand meta is the full 0–20 km circle —
 * subtract primary. Fall back to appended cards when meta is missing or
 * equals the pre-expand primary count.
 */
export const getNearbyResultsCount = ({
  metaCount,
  primaryCount,
  itemsLength,
  splitIndex,
  filters,
  region,
  enabled = false,
  isChinaApp = false,
  isWhitelabel = false,
  isApp = false,
  isMiniProgram = false,
}: {
  metaCount?: number;
  primaryCount?: number | null;
  itemsLength: number;
  splitIndex: number;
  filters?: GeoFilters;
  region?: GeoRegion;
  enabled?: boolean;
  isChinaApp?: boolean;
  isWhitelabel?: boolean;
  isApp?: boolean;
  isMiniProgram?: boolean;
}) => {
  const appended = Math.max(0, itemsLength - (splitIndex || 0));
  const meta = Number(metaCount) || 0;
  if (
    isExpandMetaNearbyBandOnly({
      filters,
      region,
      enabled,
      isChinaApp,
      isWhitelabel,
      isApp,
      isMiniProgram,
    })
  ) {
    return Math.max(meta, appended);
  }
  return Math.max(0, meta - (primaryCount || 0), appended);
};

export const hasReachedEndOfSearchList = ({
  itemsLength,
  metaCount,
  next,
  isPaginated,
  currentPage,
}: {
  itemsLength: number;
  metaCount?: number;
  next?: number | null;
  isPaginated?: boolean;
  currentPage?: number;
}) => {
  if (!itemsLength) return false;
  if (isPaginated) {
    const totalPages = Math.ceil((metaCount || 0) / 10);
    return totalPages <= 1 || (currentPage || 1) >= totalPages;
  }
  if (typeof next === "number" && next > 0) return false;
  return itemsLength >= (metaCount || itemsLength);
};

export const getExpandAvailableCount = (meta?: { expand_available_count?: number | string }) => {
  const count = Number(meta?.expand_available_count);
  if (!Number.isFinite(count) || count <= 0) return 0;
  return Math.floor(count);
};

/**
 * "N+" is a strict lower bound, floored to tens under 100 and hundreds from 100 up.
 * 47 → 40+, 110 → 100+, never 47 → 20+ or 110 → 110+.
 */
export const formatExpandAvailableCountLabel = (count: number): string => {
  if (count <= 0) return "";
  if (count < 10) return String(count);

  const step = count >= 100 ? 100 : 10;
  const bound = Math.floor((count - 1) / step) * step;
  if (bound < step) return String(count);
  return `${bound}+`;
};

type RadiusBannerArgs = {
  filters?: GeoFilters;
  region?: GeoRegion;
  itemsLength: number;
  isLoading?: boolean;
  isExpanded?: boolean;
  metaCount?: number;
  expandAvailableCount?: number;
  next?: number | null;
  isPaginated?: boolean;
  currentPage?: number;
  hasNonGeoFilters?: boolean;
  enabled?: boolean;
  /** `SEARCH_RECOMMENDED_BEYOND_FILTERS` flag: off hides the limited-matches state entirely. */
  limitedMatchesEnabled?: boolean;
};

export const shouldShowRadiusExpandBanner = (args: RadiusBannerArgs) => {
  const {
    filters,
    region,
    itemsLength,
    isLoading,
    isExpanded,
    metaCount,
    expandAvailableCount,
    next,
    isPaginated,
    currentPage,
    hasNonGeoFilters,
    enabled = false,
  } = args;
  if (isLoading) return false;

  // `enabled` only decides whether the geo "nearby" flow exists. The filter-focused states
  // below apply on every build (P1D-5464 FR-34), so a disabled flow is just "not geo".
  const isGeoFlow = isGeoRadiusExpandFlow(filters, region, enabled);

  if (!itemsLength) {
    // A filter applied after expanding can still zero the list out; expanding further
    // isn't an option at that point, so a non-geo-filter cause always wins here.
    if (Boolean(hasNonGeoFilters) && (isExpanded || !isGeoFlow)) return true;
    if (isGeoFlow && !isExpanded) return true;
    return false;
  }

  // Some matches, but nothing else within 20 km still matches the filters.
  if (isLimitedMatchesRadiusBanner(args)) return true;

  if (isExpanded || !isGeoFlow || !expandAvailableCount) return false;
  return hasReachedEndOfSearchList({
    itemsLength,
    metaCount,
    next,
    isPaginated,
    currentPage,
  });
};

/** Upper bound on results for the "limited matches" banner — 15 or fewer (P1D-5464; FR-37 said fewer than 7, raised to 10, then 15). */
export const LIMITED_MATCHES_MAX_RESULTS = 15;

/**
 * Filters matched only a few places (1..15). For a geo flow this additionally requires that
 * widening the radius would add nothing (expand_available_count 0), so the "view nearby" CTA
 * still wins while it can help. For any other filter set the state applies directly (FR-34);
 * `enabled` only decides whether the geo flow exists, it no longer gates the banner itself.
 */
export const isLimitedMatchesRadiusBanner = ({
  filters,
  region,
  itemsLength,
  isLoading,
  isExpanded,
  metaCount,
  expandAvailableCount,
  next,
  isPaginated,
  currentPage,
  hasNonGeoFilters,
  enabled = false,
  limitedMatchesEnabled = false,
}: RadiusBannerArgs) => {
  if (!limitedMatchesEnabled) return false;
  if (isLoading || isExpanded || !itemsLength || !hasNonGeoFilters) return false;
  const isGeoFlow = isGeoRadiusExpandFlow(filters, region, enabled);
  if (isGeoFlow && Number(expandAvailableCount) > 0) return false;
  // Only for a small result set (1..15). More than that isn't "limited".
  const totalMatches = Number(metaCount) || itemsLength;
  if (totalMatches > LIMITED_MATCHES_MAX_RESULTS) return false;
  return hasReachedEndOfSearchList({
    itemsLength,
    metaCount,
    next,
    isPaginated,
    currentPage,
  });
};

/**
 * Expand fetch is in flight but nearby inventories haven't been appended yet.
 * Keep the CTA banner mounted (with a loading state) so layout doesn't jump to
 * the lead form before results arrive.
 */
export const isRadiusExpandPending = ({
  isExpanded,
  isLoading,
  itemsLength,
  splitIndex,
}: {
  isExpanded?: boolean;
  isLoading?: boolean;
  itemsLength: number;
  splitIndex: number;
}) => Boolean(isExpanded && isLoading && itemsLength <= splitIndex);

export const isEmptyGeoRadiusBanner = ({
  filters,
  region,
  itemsLength,
  isLoading,
  isExpanded,
  hasNonGeoFilters,
  expandAvailableCount,
  enabled = false,
}: {
  filters?: GeoFilters;
  region?: GeoRegion;
  itemsLength: number;
  isLoading?: boolean;
  isExpanded?: boolean;
  hasNonGeoFilters?: boolean;
  expandAvailableCount?: number;
  enabled?: boolean;
}) => {
  if (!enabled || isLoading || isExpanded || itemsLength !== 0) return false;
  if (!isGeoRadiusExpandFlow(filters, region, enabled)) return false;
  // When non-geo filters are applied and there is nothing to expand to
  // (expand_available_count = 0), expanding the radius won't help, so defer to the
  // no-matches view instead of the "view nearby properties" empty-geo view.
  if (Boolean(hasNonGeoFilters) && Number(expandAvailableCount) <= 0) return false;
  return true;
};

export const isFilteredEmptyRadiusBanner = ({
  filters,
  region,
  itemsLength,
  isLoading,
  isExpanded,
  hasNonGeoFilters,
  expandAvailableCount,
  enabled = false,
}: {
  filters?: GeoFilters;
  region?: GeoRegion;
  itemsLength: number;
  isLoading?: boolean;
  isExpanded?: boolean;
  hasNonGeoFilters?: boolean;
  expandAvailableCount?: number;
  enabled?: boolean;
}) => {
  // `enabled` decides whether a geo flow exists, not whether the filter banner may show
  // (P1D-5464 FR-34 — the state applies to any filter, on every build).
  if (isLoading || itemsLength !== 0 || !hasNonGeoFilters) return false;
  // Already expanded, or not a geo flow — filters are the only lever left.
  if (isExpanded || !isGeoRadiusExpandFlow(filters, region, enabled)) return true;
  // Geo flow with nothing to expand to: expanding won't help, so treat it as
  // a filter problem instead of the empty-geo "view nearby" state.
  return Number(expandAvailableCount) <= 0;
};

/**
 * The `inventory_filter_count` API returns the radius (in metres) its counts cover
 * as `region_radius` (e.g. 5000). We surface that in the radius-expand banner copy
 * instead of a hardcoded "3 km" so the headline/eyebrow always match the API.
 *
 * Returns a whole-kilometre string ("5"). Sub-kilometre radii round up to at least
 * "1" so we never show "0 km". Returns "" for a missing/invalid value so callers can
 * fall back to their default copy.
 */
export const formatRadiusDistanceKm = (meters?: number | string): string => {
  const value = Number(meters);
  if (!Number.isFinite(value) || value <= 0) return "";
  return String(Math.max(1, Math.round(value / 1000)));
};

const METERS_PER_MILE = 1609.34;

/** Whole miles. Sub-mile radii round up to at least "1" so we never show "0 miles". */
export const formatRadiusDistanceMiles = (meters?: number | string): string => {
  const value = Number(meters);
  if (!Number.isFinite(value) || value <= 0) return "";
  return String(Math.max(1, Math.round(value / METERS_PER_MILE)));
};

export type RadiusDistanceUnitKey = "Mile" | "Miles" | "Km";

/**
 * Unit follows the search-card distance strip: miles (imperial) everywhere,
 * kilometres only in the China build (which converts the strip to km).
 */
export const getRadiusDistanceDisplay = (
  meters?: number | string,
  useMiles = true,
): { distance: string; unitKey: RadiusDistanceUnitKey } => {
  const formatted = useMiles ? formatRadiusDistanceMiles(meters) : formatRadiusDistanceKm(meters);
  const distance = formatted || "3";

  let unitKey: RadiusDistanceUnitKey = "Km";
  if (useMiles) {
    unitKey = distance === "1" ? "Mile" : "Miles";
  }
  return { distance, unitKey };
};

/** Tolgee still ships "km" in these strings. Swap it when the distance strip is miles. */
export const replaceHardcodedRadiusUnit = (copy: string, unit: string, useMiles: boolean) => {
  if (!useMiles || !copy) return copy;
  return copy
    .replace(/\bKM\b/g, unit.toUpperCase())
    .replace(/\bkm\b/g, unit.toLowerCase())
    .replace(/公里/g, unit)
    .replace(/กม\./g, unit);
};

export const getPreservedGeoFilters = (filters: GeoFilters = {}) => {
  const preserved: GeoFilters = {};
  if (filters.university) preserved.university = filters.university;
  if (filters.locality) preserved.locality = filters.locality;
  return preserved;
};
